import { OauthService } from '@app/services/oauth.service';
import { ACCT_CONSTANTS } from '@app/constants';
import { Property } from '@app/models/Property';
import { HeaderService } from '@app/components/header/header.service';
import { RegistrationApi } from '@app/services/apiServices/RegistrationApi';
import { Homeowner } from '@app/models/Homeowner';
import { NetworkErrorService } from '@app/services/NetworkError.service';
import { RegistrationStore } from './stores/RegistrationStore';
import { CompleteRegistrationRequest } from '@app/models/CompleteRegistrationRequest';
import { SpinnerService } from '@app/services/spinner.service';
import { RegistrationRequest } from '@app/models/RegistrationRequest';
import { HomeownerMarketingAgreement } from '@app/models/HomeownerMarketingAgreement';
import { PropertyApi } from '@app/services/apiServices/PropertyApi';
import { MobileLoginService } from '@app/services/MobileLogin.service';
import { UtilService } from '@app/services/Util.service';
import { take } from "rxjs/operators";
import { Component, Inject, NgZone, OnInit } from "@angular/core";
import { AppDataService } from "@app/services/appData.service";
import { MatDialog } from "@angular/material/dialog";
import { WindowToken } from "@app/util/window.token";
import { SecurityQuestionsConfirmDialog } from "@app/pages/registration/securityQuestionsConfirm.dialog";
import { PersonalInfoData } from "@app/components/personalInfo/personalInfo.directive";
import { Router } from '@angular/router';
import { ErrorHandlerService } from "@app/services/ErrorHandler.service";

export interface CreateAccountInfo {
    firstName: string;
    lastName: string;
    emailaddress: string;
    confirmEmailAddress: string;
    password: string;
    confirmPassword: string;
    username: string;
    optIn: boolean;
    passwordStrength: string;
    phone: string;
}

@Component({
    selector: 'app-create-account',
    template: require('./createAccount.html')
})
export class CreateAccountComponent implements OnInit {
    accountInfo: CreateAccountInfo;
    property: Property;
    homeowner: Homeowner;

    showPassword = false;

    personalInfoData: PersonalInfoData;

    showPasswordInvalidMessage = false;
    strengthMeterStyle = "";
    strengthTextStyle = "";
    passwordStrength = "";
    passwordsMatch = true;
    hasPasswordInputBlurred = false;

    firstNameError: string;
    lastNameError: string;
    confirmEmailAddressError: string;
    emailsMatch: boolean = true;
    passwordError: string;
    confirmPasswordError: string;
    confirmFocused: boolean;

    upperCaseCharExists: boolean;
    lowerCaseCharExists: boolean;
    numericCharExists: boolean;
    specialCharExists: boolean;
    invalidCharExists: boolean;
    passwordContainsFirstName: boolean;
    passwordContainsLastName: boolean;
    passwordContainsUsername: boolean;

    registrationRequest: RegistrationRequest;
    hoMarketingAgreement: HomeownerMarketingAgreement;

    constructor(
        @Inject(OauthService) private OauthService: OauthService,
        @Inject(MatDialog) private dialog: MatDialog,
        @Inject(HeaderService) private headerService: HeaderService,
        @Inject(RegistrationApi) private registrationApi: RegistrationApi,
        @Inject(NetworkErrorService) private networkErrorService: NetworkErrorService,
        @Inject(ErrorHandlerService) private errorHandlerService: ErrorHandlerService,
        @Inject(RegistrationStore) private registrationStore: RegistrationStore,
        @Inject(WindowToken) private $window: any,
        @Inject(SpinnerService) private spinnerService: SpinnerService,
        @Inject(PropertyApi) private propertyApi: PropertyApi,
        @Inject(MobileLoginService) private mobileLoginService: MobileLoginService,
        @Inject(AppDataService) private appDataService: AppDataService,
        @Inject(UtilService) private utilService: UtilService,
        @Inject(Router) private router: Router,
        @Inject(NgZone) private ngZone: NgZone
    ) {
        headerService.setDisplayRightContent(false);
    }

    ngOnInit() {

        this.spinnerService.closeSpinner();

        this.personalInfoData = {
            registeredUserEnabled: true,
            showErrorMessage: true,
            showRequired: true,
            emailAddressExists: false,
            isHoaHomeowner: false,
            registration: false,
            accountActive: false,
            emailAddressError: false,
            isValidatingEmail: false,
        }

        this.accountInfo = {
            firstName: null,
            lastName: null,
            emailaddress: null,
            confirmEmailAddress: null,
            password: null,
            confirmPassword: null,
            username: null,
            optIn: false,
            passwordStrength: "EMPTY",
            phone: null
        };

        if (this.registrationStore.getAccountInfo() !== undefined && this.registrationStore.getAccountInfo() !== null) {
            this.accountInfo.username = this.registrationStore.getAccountInfo().username;
            this.accountInfo.firstName = this.registrationStore.getAccountInfo().firstName;
            this.accountInfo.lastName = this.registrationStore.getAccountInfo().lastName;
            this.accountInfo.emailaddress = this.registrationStore.getAccountInfo().emailaddress;
            this.accountInfo.phone = this.registrationStore.getAccountInfo().phone;
        }

        if (this.registrationStore.getHomeowner() !== undefined && this.registrationStore.getHomeowner() !== null) {
            this.homeowner = this.registrationStore.getHomeowner();
            this.personalInfoData.homeowner = this.homeowner;
            this.accountInfo.firstName = this.homeowner ? this.homeowner.firstName : "";
            this.accountInfo.lastName = this.homeowner ? this.homeowner.lastName : "";
            this.accountInfo.emailaddress = this.homeowner ? this.homeowner.contactInformation.email.value : "";
            this.accountInfo.username = this.homeowner ? this.homeowner.contactInformation.email.value : "";
            this.accountInfo.phone = this.homeowner ? this.homeowner.contactInformation.phones[0].value : "";
        }
        if (this.registrationStore.getProperty() !== undefined && this.registrationStore.getProperty() !== null) {
            this.property = this.registrationStore.getProperty();
        }
        this.registrationStore.setAccountInfo(this.accountInfo);

    }

    invalid() {
        return !(!this.personalInfoData.emailAddressError &&
                    this.emailsMatch &&
                    !this.personalInfoData.isValidatingEmail &&
                    this.passwordsMatch &&
                    !this.showPasswordInvalidMessage &&
                    (this.passwordStrength === "Good" || this.passwordStrength === "Strong"));

    }

    cancel() {
        this.registrationStore.clearAllData();
        this.$window.location.replace("/");
    }

    submit() {
        this.accountInfo.username = this.personalInfoData.homeowner.contactInformation.email.value;
        this.accountInfo.emailaddress = this.personalInfoData.homeowner.contactInformation.email.value;
        this.registrationStore.setAccountInfo(this.accountInfo);
        this.registrationStore.setHomeowner(this.homeowner);
        this.createAccount();
    }

    createAccount() {
        this.spinnerService.showSpinner();

        this.prepareRegistration();

        const receivedAccountInfo = this.registrationStore.getAccountInfo();

        if (receivedAccountInfo) {
            this.registrationRequest.password = receivedAccountInfo.password;
        }
        this.hoMarketingAgreement.acceptedMarketing = this.registrationStore.getMarketingAgreement();

        let completeRegistrationRequest: CompleteRegistrationRequest = {
            homeowner: this.prepareHomeowner(),
            properties: [this.propertyApi.formatProperty(this.registrationStore.getProperty())],
            amuiRegistrationRequest: this.registrationRequest,
            marketingAgreement: this.hoMarketingAgreement
        };

        this.registrationApi.completeRegisterUser(completeRegistrationRequest)
            .pipe(take(1)).subscribe({
                next: () => {
                    this.spinnerService.closeSpinner();
                    this.registrationStore.clearAllData();
                    this.showRegistrationCompleteDialog();
                },
                error: error => {
                    this.spinnerService.closeSpinner();
                    if (error.error.errors && error.error.errors.length > 0) {
                        let errorMessage = '';
                        if (error.error.message) {
                            errorMessage = error.error.message;
                        }
                        error.error.errors.forEach(msg => {
                            errorMessage += '<br><br>' + msg;
                        })
                        this.errorHandlerService.handleNetworkError(error, true, errorMessage)
                            .pipe().subscribe(() =>  {
                            })
                    } else {
                        this.networkErrorService.handleNetworkError(error, true)
                            .pipe(take(1)).subscribe((resp) => {
                                if (resp === true) {
                                    this.submit();
                                }
                            });
                    }
                } });
    }

    isEmpty(value) {
        return !(value && value.length > 0);

    }

    getInvalidPasswordMsg(password, confirmPassword, userId) {
        let invalidPasswordMsg = ""; // NOSONAR: This isn't a password...

        if (password !== confirmPassword) {
            this.passwordsMatch = false;
            invalidPasswordMsg = ACCT_CONSTANTS().PW_MISMATCH;
        } else if (password === confirmPassword) {
            this.passwordsMatch = true;
        }

        if (this.isEmpty(password) || (confirmPassword && this.isEmpty(confirmPassword))) {
            return ACCT_CONSTANTS().FILL_IN_ALL_FIELDS;
        } else if (!this.isValidPasswordNoConfirm(password, userId)) {
            return this.getInitialInvalidPasswordMsg(password, userId);
        }

        return invalidPasswordMsg;
    }

    isValidPasswordNoConfirm(password, userId) {
        return this.getInitialInvalidPasswordMsg(password, userId).length === 0;
    }

    analyzePassword() {
        // Get password strength
        this.setProgressClass(this.getPasswordStrength(this.accountInfo.password));
        if (this.accountInfo.password && this.accountInfo.confirmPassword === null) {

            // do not show invalid message if password is empty
            this.showPasswordInvalidMessage = false;
        } else {
            // Password validation
            this.showPasswordInvalidMessage =
                !this.isValidPassword(this.accountInfo.password,
                    this.accountInfo.confirmPassword,
                    this.accountInfo.emailaddress);
        }
    }

    setProgressClass(strength) {
        // Make sure all of the styles are removed
        this.strengthMeterStyle = "HOA-progress-state-invalid";
        this.strengthTextStyle = "";

        // apply style based on passwordStrength
        if ("Too Short" === strength) {
            this.strengthTextStyle = "u-fontRed";
            this.strengthMeterStyle = "HOA-progress-state-too-short";
        } else if ("Weak" === strength) {
            this.strengthTextStyle = "u-fontRed";
            this.strengthMeterStyle = "HOA-progress-state-weak";
        } else if ("Fair" === strength) {
            this.strengthTextStyle = "u-fontOrange";
            this.strengthMeterStyle = "HOA-progress-state-fair";
        } else if ("Good" === strength) {
            this.strengthTextStyle = "u-fontLime";
            this.strengthMeterStyle = "HOA-progress-state-good";
        } else if ("Strong" === strength) {
            this.strengthTextStyle = "fontLimeDarker";
            this.strengthMeterStyle = "HOA-progress-state-strong";
        } else if ("Strength" === strength) {
            this.strengthTextStyle = "empty";
            this.strengthMeterStyle = "HOA-progress-state-empty";
        } else if ("Invalid Character" === strength) {
            this.strengthTextStyle = "invalidchar";
            this.strengthMeterStyle = "HOA-progress-state-invalid";
        }

        this.accountInfo.passwordStrength = strength;
    }


    getPasswordStrength(password) {
        /*
         * Length: 8-32 characters. Composition: Allowed characters: Aa-Zz, 0-9, -, (_), /, (), (@), ($), !, (#) passsword cannot
         * equal or contain the userId. minimum of two character classes (lower-alpha, upper-alpha, numeric, special character).
         */

        let strength = null;

        if (password === null || password === undefined || password.length < 1) {
            strength = "Empty";
            this.upperCaseCharExists = false;
            this.lowerCaseCharExists = false;
            this.numericCharExists = false;
            this.specialCharExists = false;
            this.invalidCharExists = false;
        }

        if (strength === null) {
            // get Character classes
            this.upperCaseCharExists = false;
            this.lowerCaseCharExists = false;
            this.numericCharExists = false;
            this.specialCharExists = false;
            this.invalidCharExists = false;

            for (let x = 0; x < password.length; x++) {
                if (password.charAt(x) >= 'A' && password.charAt(x) <= 'Z') {
                    this.upperCaseCharExists = true;
                } else if (password.charAt(x) >= 'a' && password.charAt(x) <= 'z') {
                    this.lowerCaseCharExists = true;
                } else if (password.charAt(x) >= '0' && password.charAt(x) <= '9') {
                    this.numericCharExists = true;
                } else if (password.charAt(x) === '-' || password.charAt(x) === '_' || password.charAt(x) === '/'
                    || password.charAt(x) === '@' || password.charAt(x) === '$'
                    || password.charAt(x) === '#' || password.charAt(x) === '!') {
                    this.specialCharExists = true;
                } else {
                    this.invalidCharExists = true;
                }
            }

            let strengthVal = 0;
            if (this.upperCaseCharExists) {
                strengthVal++;
            }
            if (this.lowerCaseCharExists) {
                strengthVal++;
            }
            if (this.numericCharExists) {
                strengthVal++;
            }
            if (this.specialCharExists) {
                strengthVal++;
            }
            if (password.length >= 13) {
                strengthVal++;
            }

            if (this.invalidCharExists) {
                strength = "Invalid Character";
            } else if (password.length < 8) {
                strength = "Too Short";
            } else if (strengthVal <= 2) {
                strength = "Weak";
            } else if (strengthVal === 3) {
                strength = "Fair";
            } else if (strengthVal === 4) {
                strength = "Good";
            } else {
                // Strength is 5 or greater
                strength = "Strong";
            }
        }
        this.passwordStrength = strength;
        return strength;
    }

    isValidPassword(password, confirmPassword, userId) {
        return this.getInvalidPasswordMsg(password, confirmPassword, userId).length === 0;
    }

    getInitialInvalidPasswordMsg(password, userId) {
        /*
         * Length: > 13 characters. Composition: Allowed characters: Aa-Zz, 0-9, -, (_), /, (), (@), ($), !, (#) passsword cannot
         * equal or contain the userId. minimum of two character classes (lower-alpha, upper-alpha, numeric, special character).
         *
         * Enterprise LDAP web service will not allow any argument with a wildcard (defined as '*'), so '*' effectively are
         * prohibited from user passwords.
         */
        let invalidPasswordMsg = ""; // NOSONAR: This isn't a password...
        if (password && password.length < 13 && password.length !== 0) {
            invalidPasswordMsg = ACCT_CONSTANTS().PW_INVALID_TOO_SHORT;
        }

        // okta - cannot contain first name
        this.passwordContainsFirstName = false;
        if (this.utilService.hasName(password, this.accountInfo.firstName)) {
            this.passwordContainsFirstName = true;
            invalidPasswordMsg = ACCT_CONSTANTS().PW_INVALID_CONTAINS_FIRST_NAME;
        }

        // okta - cannot contain last name
        this.passwordContainsLastName = false;
        if (this.utilService.hasName(password, this.accountInfo.lastName)) {
            this.passwordContainsLastName = true;
            invalidPasswordMsg = ACCT_CONSTANTS().PW_INVALID_CONTAINS_LAST_NAME;
        }

        // okta - cannot contain part of username

        this.passwordContainsUsername = this.utilService.hasPartOfEmail(password, userId);

        // validate invalidPasswordMsg - could've been set during char by char check above
        if (this.passwordContainsUsername) {
            invalidPasswordMsg = ACCT_CONSTANTS().PW_INVALID_CONTAINS_USER_NAME;
        }

        if (invalidPasswordMsg.length === 0) {
            // get Character classes
            this.upperCaseCharExists = false;
            this.lowerCaseCharExists = false;
            this.numericCharExists = false;
            this.specialCharExists = false;
            this.invalidCharExists = false;

            for (let x = 0; x < password.length; x++) {
                if (password.charAt(x) >= 'A' && password.charAt(x) <= 'Z') {
                    this.upperCaseCharExists = true;
                } else if (password.charAt(x) >= 'a' && password.charAt(x) <= 'z') {
                    this.lowerCaseCharExists = true;
                } else if (password.charAt(x) >= '0' && password.charAt(x) <= '9') {
                    this.numericCharExists = true;
                } else if (password.charAt(x) === '-' || password.charAt(x) === '_' || password.charAt(x) === '/'
                    || password.charAt(x) === '@' || password.charAt(x) === '$' || password.charAt(x) === '#'
                    || password.charAt(x) === '!') {
                    this.specialCharExists = true;
                } else {
                    this.invalidCharExists = true;
                    // Don't need to check the rest of the password at this point
                    // break;
                }
            }
            let charClassCount = 0;
            if (this.upperCaseCharExists) {
                charClassCount++;
            }
            if (this.lowerCaseCharExists) {
                charClassCount++;
            }
            if (this.numericCharExists) {
                charClassCount++;
            }
            if (this.specialCharExists) {
                charClassCount++;
            }
            if (this.invalidCharExists) {
                invalidPasswordMsg = ACCT_CONSTANTS().PW_INVALID_CHARACTERS;
            } else if (charClassCount < 4 && password.length !== 0) {
                invalidPasswordMsg = ACCT_CONSTANTS().PW_INVALID_ONE_CHAR_CLASS;
            }
        }

        if (invalidPasswordMsg.length === 0) {
            return "";
        } else {
            return invalidPasswordMsg;
        }

    }

    firstName() {
        if (this.accountInfo.firstName === null) {
            this.firstNameError = "Please enter your first name.";
        } else {
            this.firstNameError = null;

            this.accountInfo.firstName = this.enforceCharacterLimit(this.accountInfo.firstName, 25)
            this.registrationStore.setAccountInfo(this.accountInfo);
        }
    }

    lastName() {
        if (this.accountInfo.lastName === null) {
            this.lastNameError = "Please enter your last name.";
        } else {
            this.lastNameError = null;

            this.accountInfo.lastName = this.enforceCharacterLimit(this.accountInfo.lastName, 35)
            this.registrationStore.setAccountInfo(this.accountInfo);
        }
    }

    enforceCharacterLimit(text, limit): string {
        if (text.length > limit) {
            text = text.substring(0, limit);
        }

        return text
    }

    confirmEmailAddress() {
        if (this.accountInfo.confirmEmailAddress === null) {
            this.confirmEmailAddressError = "Please confirm your email address.";
        } else if (this.personalInfoData.homeowner.contactInformation.email.value !== null &&
                    this.accountInfo.confirmEmailAddress !== null &&
                    this.personalInfoData.homeowner.contactInformation.email.value !== this.accountInfo.confirmEmailAddress) {
            this.confirmEmailAddressError = "Email addresses do not match.";
            this.emailsMatch = false;
        } else {
            this.confirmEmailAddressError = null;
            this.emailsMatch = true;
        }
    }

    valid(pattern, value) {
        return pattern.test(value);
    }

    password() {
        if (this.accountInfo.password === null)
            this.passwordError = "Please enter your password";
        else
            this.passwordError = null;
        this.registrationStore.setAccountInfo(this.accountInfo);
    }

    confirmPassword() {
        this.confirmFocused = true;
        if (this.accountInfo.confirmPassword === null)
            this.confirmPasswordError = "Please confirm your password";
        else
            this.confirmPasswordError = null;
        this.registrationStore.setAccountInfo(this.accountInfo);
    }

    prepareRegistration() {
        this.registrationRequest = this.registrationStore.getRegistrationRequest();

        if (this.registrationRequest == null) {
            const accountInfo = this.registrationStore.getAccountInfo()
            const homeowner = this.registrationStore.getHomeowner()
            this.registrationRequest = {
                emailAddress: accountInfo.emailaddress,
                firstName: homeowner.firstName,
                lastName: homeowner.lastName,
                username: accountInfo.username,
                password: "",
                homeownerId: homeowner.id,
                securityQuestionAnswers: null,
                existing: false,
                phone: accountInfo.phone
            }
        }

        this.hoMarketingAgreement = {
            acceptedMarketing: false,
            acceptedMaketingDate: "",
            partyId: null
        }

    }

    prepareHomeowner() {
        if (this.registrationStore.getHomeowner()) {

            const homeowner = this.registrationStore.getHomeowner()
            let phones = homeowner.contactInformation.phones;

            const propAddr = this.registrationStore.getProperty().address;

            homeowner.contactInformation.phones = [{
                ...phones[0],
                type: "home",
            }]

            homeowner.contactInformation.mailingAddress = {
                line1: propAddr.line1,
                line2: propAddr.line2,
                unit: propAddr.unit,
                postalCode: propAddr.postalCode,
                state: propAddr.state,
                city: propAddr.city,
                country: propAddr.country || "USA",
            }

            homeowner.contactInformation.email = this.personalInfoData.homeowner.contactInformation.email;
            homeowner.contactInformation.amuiId = this.registrationRequest.username;
            return homeowner;
        }

        return this.homeowner;
    }

    showRegistrationCompleteDialog() {
        const dialogRef = this.dialog.open(SecurityQuestionsConfirmDialog)
        dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
            // logout in the unlikely event of a previously logged-in user existing during a new or GP registration.
            this.$window.location.href = this.appDataService.appConfig.get_APP_ENV().HOAPASS_WEB_URL + "/registered/#/properties";
        }, () => {})
    }
}

